/*
* p-headerDefault
*/

import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock-upgrade';
import { ScrollTrigger } from "gsap/dist/ScrollTrigger";
import { createFocusTrap } from 'focus-trap';

const pHeaderDefault = {
    cfg: {
        name: 'Header +',
        selectors: {
            root: '.p-headerDefault',
            menuBtn: '.js-menu-btn',
            overlay: '.js-overlay',
            navLinks: '.js-navLinkBtn',
        },
        classes: {
            isOpen: 'is-open',
            isAtTop: 'is-at-top',
            isActive: 'is-active',
            isVisible: 'is-visible',
        },
        el: {
            $header: undefined,
            $banderole: undefined,
            $menuBtn: undefined,
            $overlay: undefined,
            $gsap: window.gsap,
        },
        overlayTrap: undefined,
    },

    isOpen() {
        return this.cfg.el.$header.classList.contains(this.cfg.classes.isOpen);
    },

    setElements() {
        this.cfg.el.$header = document.querySelector(this.cfg.selectors.root);
        
        if (this.cfg.el.$header) {
            this.cfg.el.$overlay = this.cfg.el.$header.querySelector(this.cfg.selectors.overlay);
            this.cfg.el.$menuBtn = [...this.cfg.el.$header.querySelectorAll(this.cfg.selectors.menuBtn)];
        }
    },

    initScrollTrigger() {
        this.cfg.el.$header.classList.add(this.cfg.classes.isAtTop);

        this.cfg.el.$gsap.to(this.cfg.el.$header, {
            scrollTrigger: {
                trigger: document.querySelector('body > header') ?? this.cfg.el.$header,
                start: "bottom start",
                end: "bottom start",
                onEnter: () => { 
                    this.cfg.el.$header.classList.remove(this.cfg.classes.isAtTop);
                },
                onEnterBack: () => { 
                    this.cfg.el.$header.classList.add(this.cfg.classes.isAtTop);
                }
            },
        });

        ScrollTrigger.refresh();
    },

    handleBodyScrollLock() {
        if(this.isOpen()) {
            return enableBodyScroll(document.body); 
        }

        disableBodyScroll(document.body, { 
            allowTouchMove: el => {
                while (el && el !== document.body) {
                if (el.getAttribute('body-scroll-lock-ignore') !== null) {
                    return true;
                }
            
                el = el.parentElement;
                }
            }
        });
    },

    toggleMenu(_btn) {
        if (this.cfg.overlayTrap._toggling) {
            return;
        }
        
        this.cfg.overlayTrap._toggling = true;
        this.handleBodyScrollLock();
        const isOpen = this.isOpen();

        if (isOpen) {
            this.cfg.overlayTrap.deactivate();
            this.cfg.el.$overlay.inert = true;
            _btn.ariaExpanded = false;

            // reset to the init state when closing
            const links = this.cfg.el.$header.querySelectorAll(this.cfg.selectors.navLinks);
            links.forEach((link) => {
                const submenu = link.nextElementSibling;
                if (!submenu) return;

                this.updateLink(link, submenu.querySelector(`.${this.cfg.classes.isActive}`));
            });
        } else {
            this.cfg.el.$overlay.inert = false;
            this.cfg.overlayTrap.activate();
            _btn.ariaExpanded = true;
        }

        this.cfg.el.$header.classList.toggle(this.cfg.classes.isOpen);
        this.cfg.overlayTrap._toggling = false;
    },

    updateLink(link, activate) {
        const submenu = link.nextElementSibling;
        submenu.style.top = '';
        if (activate) {
            const distance = Math.round(160 - (window.innerHeight - submenu.getBoundingClientRect().bottom));
            if (distance > 0) {
                submenu.style.top = `-${distance}px`;
            }
        }

        link.parentNode.classList[activate ? 'add' : 'remove'](this.cfg.classes.isOpen);
        link.ariaExpanded = activate;
        link.nextElementSibling.inert = !activate;
    },

    addEventListeners() {
        this.cfg.el.$menuBtn?.forEach(btn => {
            btn.addEventListener('click', () => {
                this.cfg.overlayTrap._btn = btn;
                this.toggleMenu(btn);
            });
        });

        // scroll trigger
        this.initScrollTrigger();

        const links = this.cfg.el.$header.querySelectorAll(this.cfg.selectors.navLinks);
        links.forEach((link) => {
            const submenu = link.nextElementSibling;
            if (!submenu) return;

            if (submenu.querySelector(`.${this.cfg.classes.isActive}`)) {
                this.updateLink(link, true);
            }

            link.addEventListener('click', (e) => {
                e.preventDefault();
                const alreadyOpen = link.parentNode.classList.contains(this.cfg.classes.isOpen);
                for (const otherLink of links) {
                    this.updateLink(otherLink, false);
                }
                
                if (alreadyOpen) {
                    return;
                }
                this.updateLink(link, true);
            });
        });
    },

    init() {
        console.debug(`Init: ${this.cfg.name}`);
        this.setElements();

        if (this.cfg.el.$header) {
            this.cfg.overlayTrap = createFocusTrap(this.cfg.el.$header, {
                onDeactivate: () => this.toggleMenu(this.cfg.overlayTrap._btn),
            });
            this.addEventListeners();
        }
    },
};

export default pHeaderDefault;
